'use client'

import { Tooltip as TooltipPrimitive } from 'radix-ui'

import { cn } from '@playbooks/ui/lib/utils'

/**
 * A wrapper around the TooltipContent component that allows it to be rendered in a different container.
 * This is useful for rendering the tooltip content in the shadow DOM
 *
 * @param {object} props - The props for the TooltipContentWithContainer component.
 * @param {HTMLElement} [props.container] - The container element to render the tooltip content in.
 *
 * (Other props are described in the TooltipPrimitive.Content.Props and TooltipPrimitive.Portal.Props interfaces)
 * @returns {JSX.Element} The rendered TooltipContent component wrapped in a portal.
 */
function TooltipContentWithContainer({
  className,
  sideOffset = 0,
  children,
  container,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> &
  Pick<React.ComponentProps<typeof TooltipPrimitive.Portal>, 'container'>) {
  return (
    <TooltipPrimitive.Portal container={container}>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 rounded-md px-3 py-1.5 text-xs bg-foreground text-background z-50 w-fit max-w-xs origin-(--radix-tooltip-content-transform-origin)',
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="size-2.5 rotate-45 rounded-[2px] bg-foreground fill-foreground z-50 translate-y-[calc(-50%_-_2px)]" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { TooltipContentWithContainer }
